import React, { useState, useEffect, useRef } from 'react';
import { Heart, MessageCircle, Eye, TrendingUp, Flame, Award, Users, Send, Clock, ChevronUp, ChevronDown, Share2, Bookmark, Filter, Search, Sparkles, Zap, Star, Trophy, Gift, Bell, Plus, X, Reply, MoreHorizontal, MessageSquare, UserCircle, ArrowUp, ArrowDown, Image as ImageIcon } from 'lucide-react';
import API_URL from '../config/ApiUrl';
import { formatContentWithHashtags } from '../utils/hashtagUtils';
import PublicProfileView from '../profile_component/PublicProfileViewPage';
import { getSmokerEmoji } from '../static/SmokerEmoji';
import { useTheme } from '../arrangement/ThemeContext';

const GIPHY_API_KEY = process.env.REACT_APP_GIPHY_API_KEY; // Replace with your actual key



const ThreadDetailView = ({ thread, onClose, currentUserId }) => {
  const { colors } = useTheme();
    
  console.log("THREAD: ,", thread)
  const [threadData, setThreadData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeHashtag, setActiveHashtag] = useState(null);
  const [showProfileView, setShowProfileView] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  
  // GIPHY states
  const [showGiphyPicker, setShowGiphyPicker] = useState(false);
  const [giphySearch, setGiphySearch] = useState('');
  const [giphyResults, setGiphyResults] = useState([]);
  const [giphyLoading, setGiphyLoading] = useState(false);
  const [selectedGif, setSelectedGif] = useState(null);
  const [isReplyGiphy, setIsReplyGiphy] = useState(false);

  // Mobile detection
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch detailed thread data
  useEffect(() => {
    const fetchThreadDetails = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_URL}/api/get-thread/${thread.id}/${currentUserId}`, {
          credentials: 'include'
        });
        const data = await response.json();
        console.log('The Data: ', data);
        setThreadData(data);
      } catch (error) {
        console.error('Failed to fetch thread details:', error);
      } finally {
        setLoading(false);
      }
    };

    if (thread?.id && currentUserId) {
      fetchThreadDetails();
    }
  }, [thread?.id, currentUserId]);

  // Search GIPHY
  const searchGiphy = async (query) => {
    if (!query.trim()) {
      // Load trending if no search query
      loadTrendingGifs();
      return;
    }

    setGiphyLoading(true);
    try {
      const response = await fetch(
        `https://api.giphy.com/v1/gifs/search?api_key=${GIPHY_API_KEY}&q=${encodeURIComponent(query)}&limit=20&rating=g`
      );
      const data = await response.json();
      setGiphyResults(data.data || []);
    } catch (error) {
      console.error('Failed to search GIPHY:', error);
    } finally {
      setGiphyLoading(false);
    }
  };

  // Load trending GIFs
  const loadTrendingGifs = async () => {
    setGiphyLoading(true);
    try {
      const response = await fetch(
        `https://api.giphy.com/v1/gifs/trending?api_key=${GIPHY_API_KEY}&limit=20&rating=g`
      );
      const data = await response.json();
      setGiphyResults(data.data || []);
    } catch (error) {
      console.error('Failed to load trending GIFs:', error);
    } finally {
      setGiphyLoading(false);
    }
  };

  // Open GIPHY picker
  const openGiphyPicker = (forReply = false) => {
    setIsReplyGiphy(forReply);
    setShowGiphyPicker(true);
    loadTrendingGifs();
  };

  // Select GIF
  const selectGif = (gif) => {
    setSelectedGif(gif);
    setShowGiphyPicker(false);
    
    if (isReplyGiphy) {
      setReplyContent(gif.id);
    } else {
      setNewMessage(gif.id);
    }
  };

  // Clear selected GIF
  const clearSelectedGif = () => {
    setSelectedGif(null);
    if (isReplyGiphy) {
      setReplyContent('');
    } else {
      setNewMessage('');
    }
  };

  const handleUsernameClick = (userId) => {
    if (userId && userId !== currentUserId) {
      setSelectedUserId(userId);
      setShowProfileView(true);
    }
  };

  const handleVote = async (value) => {
    try {
      await handleVoteThread(value);
    } catch (error) {
      console.error('Failed to vote:', error);
    }
  };

  const handleHashtagClick = (tag) => {
    setActiveHashtag(tag);
    setSearchTerm(`#${tag}`);
  };

  const handleVoteThread = async (value) => {
    try {
      const response = await fetch(`${API_URL}/api/thread/vote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          thread_id: thread.id,
          user_id: currentUserId,
          value: value
        })
      });
      const data = await response.json();

      setThreadData(prev => ({
        ...prev,
        user_vote: value,
        likes: data.total_likes
      }));
    } catch (error) {
      console.error('Failed to vote on thread:', error);
    }
  };

  const handleVoteMessage = async (messageId, value) => {
    try {
      const response = await fetch(`${API_URL}/api/thread-reply/vote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          message_id: messageId,
          user_id: currentUserId,
          value: value
        })
      });
      const data = await response.json();

      const updateMessageVote = (messages) => {
        return messages.map(msg => {
          if (msg.id === messageId) {
            return {
              ...msg,
              user_vote: value,
              likes: data.total_likes
            };
          }
          if (msg.replies && msg.replies.length > 0) {
            return {
              ...msg,
              replies: updateMessageVote(msg.replies)
            };
          }
          return msg;
        });
      };

      setThreadData(prev => ({
        ...prev,
        messages: updateMessageVote(prev.messages)
      }));
    } catch (error) {
      console.error('Failed to vote on message:', error);
    }
  };

  const handlePostMessage = async () => {
    const messageContent = newMessage.trim();
    if (!messageContent) return;

    // Determine if it's a GIF or text
    const contentType = selectedGif ? 'gif' : 'text';

    try {
      const response = await fetch(`${API_URL}/api/add-thread-message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          thread_id: thread.id,
          user_id: currentUserId,
          content: messageContent,
          content_type: contentType
        })
      });
      const data = await response.json();

      setThreadData(prev => ({
        ...prev,
        messages: [...prev.messages, data]
      }));

      setNewMessage('');
      setSelectedGif(null);
    } catch (error) {
      console.error('Failed to post message:', error);
    }
  };

  const handlePostReply = async () => {
    const content = replyContent.trim();
    if (!content || !replyingTo) return;

    // Determine if it's a GIF or text
    const contentType = isReplyGiphy && selectedGif ? 'gif' : 'text';

    try {
      const response = await fetch(`${API_URL}/api/add-thread-message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          thread_id: thread.id,
          user_id: currentUserId,
          reply_to_id: replyingTo.id,
          content: content,
          content_type: contentType
        })
      });
      const data = await response.json();

      const addReplyToMessage = (messages) => {
        return messages.map(msg => {
          if (msg.id === replyingTo.id) {
            return {
              ...msg,
              replies: [...(msg.replies || []), data]
            };
          }
          if (msg.replies && msg.replies.length > 0) {
            return {
              ...msg,
              replies: addReplyToMessage(msg.replies)
            };
          }
          return msg;
        });
      };

      setThreadData(prev => ({
        ...prev,
        messages: addReplyToMessage(prev.messages)
      }));

      setReplyContent('');
      setReplyingTo(null);
      setSelectedGif(null);
    } catch (error) {
      console.error('Failed to post reply:', error);
    }
  };

  // Render message content based on type
  const renderMessageContent = (message) => {
    if (message.content_type === 'gif') {
      return (
        <div style={{
          marginBottom: '12px',
          borderRadius: '12px',
          overflow: 'hidden',
          maxWidth: isMobile ? '100%' : '400px'
        }}>
          <img
            src={`https://media.giphy.com/media/${message.content}/giphy.gif`}
            alt="GIF"
            style={{
              width: '100%',
              height: 'auto',
              display: 'block',
              borderRadius: '12px'
            }}
          />
        </div>
      );
    }

    return (
      <div style={{
        color: '#e5e5e5',
        fontSize: isMobile ? '14px' : '15px',
        lineHeight: '1.6',
        marginBottom: '12px',
        wordWrap: 'break-word'
      }}>
        {formatContentWithHashtags(message.content, handleHashtagClick)}
      </div>
    );
  };

  const MessageComponent = ({ message, depth = 0, isLastInChain = true }) => {

    const getSmokerLevelColor = (level) => {
      switch (level?.toLowerCase()) {
        case 'newbie': return '#10b981';
        case 'casual': return '#3b82f6';
        case 'regular': return '#8b5cf6';
        case 'connoisseur': return '#f59e0b';
        case 'expert': return '#ef4444';
        default: return '#6b7280';
      }
    };

    const getSmokerLevelIcon = (level) => {
      switch (level?.toLowerCase()) {
        case 'newbie': return '🌱';
        case 'casual': return '🍃';
        case 'regular': return '🌿';
        case 'connoisseur': return '🔥';
        case 'expert': return '👑';
        default: return '🚬';
      }
    };

    return (
      <div style={{
        marginLeft: isMobile ? `${depth * 12}px` : `${depth * 24}px`,
        borderLeft: depth > 0 ? '2px solid #333' : 'none',
        paddingLeft: isMobile && depth > 0 ? '8px' : depth > 0 ? '16px' : '0',
        marginBottom: depth > 0 ? '0' : '16px',
        background: colors.bgTertiary1,
        borderRadius: depth > 0 ? '8px' : '0',
        padding: depth > 0 ? '8px' : '0',
        paddingBottom: depth > 0 ? '8px' : '0'
      }}>
        <div style={{
          background: colors.bgTertiary1,
          borderRadius: '12px',
          padding: isMobile ? '12px' : '16px',
          border: '1px solid #333',
          marginBottom: '16px'
        }}>
          {/* Message Header */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '12px',
            flexWrap: isMobile ? 'wrap' : 'nowrap'
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: isMobile ? '8px' : '12px',
              flex: 1
            }}>
              {/* Profile Picture */}
              <div
                onClick={() => handleUsernameClick(message.user_id)}
                style={{
                  width: isMobile ? '28px' : '32px',
                  height: isMobile ? '28px' : '32px',
                  borderRadius: '50%',
                  cursor: message.user_id !== currentUserId ? 'pointer' : 'default',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  flexShrink: 0
                }}
                onMouseEnter={(e) => {
                  if (message.user_id !== currentUserId) {
                    e.currentTarget.style.transform = 'scale(1.1)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(139, 92, 246, 0.4)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (message.user_id !== currentUserId) {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = 'none';
                  }
                }}
              >
                <img 
                  src={message.profile_pic || message.user_avatar}
                  alt="Profile"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              </div>

              {/* Smoker Level Emoji */}
              <div style={{
                width: isMobile ? '28px' : '32px',
                height: isMobile ? '28px' : '32px',
                borderRadius: '50%',
                backgroundColor: '#111',
                border: '1px solid #00d4aa',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <img 
                  src={getSmokerEmoji(message.smoker_level)}
                  alt={`${message.smoker_level} level`}
                  style={{
                    width: isMobile ? '24px' : '28px',
                    height: isMobile ? '24px' : '28px',
                    borderRadius: '50%',
                    objectFit: 'cover'
                  }}
                />
              </div>

              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '4px' : '8px', flexWrap: 'wrap' }}>
                  <span 
                    onClick={() => handleUsernameClick(message.user_id)}
                    style={{
                      fontWeight: '600',
                      color: '#e5e5e5',
                      fontSize: isMobile ? '13px' : '14px',
                      cursor: message.user_id !== currentUserId ? 'pointer' : 'default',
                      transition: 'all 0.3s ease',
                      textDecoration: 'none',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                    onMouseEnter={(e) => {
                      if (message.user_id !== currentUserId) {
                        e.currentTarget.style.textDecoration = 'underline';
                        e.currentTarget.style.textDecorationColor = '#00d4aa';
                        e.currentTarget.style.textUnderlineOffset = '2px';
                        e.currentTarget.style.color = '#00d4aa';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (message.user_id !== currentUserId) {
                        e.currentTarget.style.textDecoration = 'none';
                        e.currentTarget.style.color = '#e5e5e5';
                      }
                    }}
                  >
                    {message.display_name || message.username}
                  </span>
                  
                  {!isMobile && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '2px 8px',
                      background: `${getSmokerLevelColor(message.smoker_level)}20`,
                      border: `1px solid ${getSmokerLevelColor(message.smoker_level)}40`,
                      borderRadius: '12px'
                    }}>
                      <span style={{ fontSize: '12px' }}>
                        {getSmokerLevelIcon(message.smoker_level)}
                      </span>
                      <span style={{
                        fontSize: '11px',
                        fontWeight: '500',
                        color: getSmokerLevelColor(message.smoker_level),
                        textTransform: 'capitalize'
                      }}>
                        {message.smoker_level || 'Member'}
                      </span>
                    </div>
                  )}
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px' }}>
                  <Clock size={isMobile ? 10 : 12} style={{ color: '#888' }} />
                  <span style={{ fontSize: isMobile ? '10px' : '12px', color: '#888' }}>
                    {new Date(message.timestamp).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
            
            {!isMobile && (
              <button style={{
                background: 'none',
                border: 'none',
                color: '#888',
                cursor: 'pointer',
                padding: '4px'
              }}>
                <MoreHorizontal size={16} />
              </button>
            )}
          </div>

          {/* Message Content - Now handles GIFs */}
          {renderMessageContent(message)}

          {/* Message Actions */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: isMobile ? 'wrap' : 'nowrap',
            gap: isMobile ? '8px' : '0'
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: isMobile ? '8px' : '12px',
              flexWrap: isMobile ? 'wrap' : 'nowrap'
            }}>
              {/* Vote Buttons */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <button
                  onClick={() => handleVoteMessage(message.id, message.user_vote === 1 ? 0 : 1)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: isMobile ? '4px 6px' : '6px 8px',
                    background: message.user_vote === 1 
                      ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                      : colors.upvote1,
                    border: '1px solid',
                    borderColor: message.user_vote === 1 ? '#10b981' : '#333',
                    borderRadius: '8px',
                    color: message.user_vote === 1 ? 'white' : '#888',
                    cursor: 'pointer',
                    fontSize: isMobile ? '11px' : '12px',
                    fontWeight: '500',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <ArrowUp size={isMobile ? 10 : 12} />
                  <span>{message.likes || 0}</span>
                </button>
                
                <button
                  onClick={() => handleVoteMessage(message.id, message.user_vote === -1 ? 0 : -1)}
                  style={{
                    padding: isMobile ? '4px' : '6px',
                    background: message.user_vote === -1 
                      ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                      : 'transparent',
                    border: '1px solid',
                    borderColor: message.user_vote === -1 ? '#ef4444' : '#333',
                    borderRadius: '8px',
                    color: message.user_vote === -1 ? 'white' : '#888',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <ArrowDown size={isMobile ? 10 : 12} />
                </button>
              </div>

              {/* Reply Button */}
              <button
                onClick={() => setReplyingTo(message)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: isMobile ? '4px 6px' : '6px 8px',
                  background: 'transparent',
                  border: '1px solid #333',
                  borderRadius: '8px',
                  color: '#888',
                  cursor: 'pointer',
                  fontSize: isMobile ? '11px' : '12px',
                  fontWeight: '500',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #8b5cf620 0%, #7c3aed20 100%)';
                  e.currentTarget.style.borderColor = '#8b5cf6';
                  e.currentTarget.style.color = '#8b5cf6';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.borderColor = '#333';
                  e.currentTarget.style.color = '#888';
                }}
              >
                <Reply size={isMobile ? 10 : 12} />
                {!isMobile && 'Reply'}
              </button>

              <button style={{
                padding: isMobile ? '4px' : '6px',
                background: 'transparent',
                border: '1px solid #333',
                borderRadius: '8px',
                color: '#888',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}>
                <Share2 size={isMobile ? 10 : 12} />
              </button>
            </div>

            {message.replies && message.replies.length > 0 && (
              <span style={{ fontSize: isMobile ? '10px' : '12px', color: '#888' }}>
                {message.replies.length} {message.replies.length === 1 ? 'reply' : 'replies'}
              </span>
            )}
          </div>
        </div>

        {/* Render Replies */}
        {message.replies && message.replies.map((reply) => (
          <MessageComponent
            key={reply.id}
            message={reply}
            depth={depth + 1}
          />
        ))}
      </div>
    );
  };

  // GIPHY Picker Component
  const GiphyPicker = () => (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)', // Semi-transparent backdrop
      zIndex: 2000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      {/* Modal Container */}
      <div style={{
        width: '100%',
        maxWidth: isMobile ? '100%' : '600px', // Smaller max width
        maxHeight: isMobile ? '100%' : '70vh', // 70% of viewport height
        background: colors.bgTertiary1,
        borderRadius: '16px',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)'
      }}>
        {/* Header */}
        <div style={{
          padding: '16px',
          borderBottom: '1px solid #333',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: colors.bgTertiary1,
          flexShrink: 0
        }}>
          <h3 style={{ margin: 0, color: 'white', fontSize: '18px' }}>Choose a GIF</h3>
          <button
            onClick={() => {
              setShowGiphyPicker(false);
              setGiphySearch('');
            }}
            style={{
              background: 'none',
              border: 'none',
              color: '#888',
              cursor: 'pointer',
              padding: '8px'
            }}
          >
            <X size={24} />
          </button>
        </div>

        {/* Search Bar */}
        <div style={{ 
          padding: '16px', 
          background: colors.bgTertiary1,
          flexShrink: 0
        }}>
          <div style={{ position: 'relative' }}>
            <Search
              size={18}
              style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#888'
              }}
            />
            <input
              type="text"
              value={giphySearch}
              onChange={(e) => {
                setGiphySearch(e.target.value);
                searchGiphy(e.target.value);
              }}
              placeholder="Search GIPHY..."
              style={{
                width: '100%',
                padding: '12px 12px 12px 40px',
                border: '1px solid #333',
                borderRadius: '12px',
                background: colors.bgCard,
                color: '#e5e5e5',
                fontSize: '16px',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
          </div>
        </div>

        {/* GIF Grid */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '16px',
          background: colors.bgPrimary
        }}>
          {giphyLoading ? (
            <div style={{ textAlign: 'center', padding: '48px', color: '#888' }}>
              Loading GIFs...
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(2, 1fr)', // 2 columns instead of 3
              gap: '12px'
            }}>
              {giphyResults.map((gif) => (
                <div
                  key={gif.id}
                  onClick={() => selectGif(gif)}
                  style={{
                    cursor: 'pointer',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    transition: 'transform 0.2s ease',
                    border: '2px solid transparent'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.05)';
                    e.currentTarget.style.borderColor = '#00d4aa';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.borderColor = 'transparent';
                  }}
                >
                  <img
                    src={gif.images.fixed_height.url}
                    alt={gif.title}
                    style={{
                      width: '100%',
                      height: 'auto',
                      display: 'block'
                    }}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <div style={{ textAlign: 'center', color: 'white' }}>
          <div style={{ fontSize: '32px', marginBottom: '16px' }}>💬</div>
          <p>Loading thread...</p>
        </div>
      </div>
    );
  }

  if (!threadData) {
    return null;
  }

  // MOBILE VIEW
  if (isMobile) {
    return (
      <>
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor:colors.bgPrimary,
          zIndex: 1000,
          overflow: 'auto'
        }}>
          <div style={{
            minHeight: '100vh',
            background: colors.bgTertiary1,
            paddingBottom: '80px'
          }}>
            {/* Mobile Header - Fixed */}
            <div style={{
              position: 'sticky',
              top: 0,
              zIndex: 10,
              background: colors.bgTertiary1,
              borderBottom: `1px solid ${colors.border}`,
              padding: '12px 16px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <h2 style={{
                  margin: 0,
                  color: 'white',
                  fontSize: '18px',
                  fontWeight: '700'
                }}>
                  Thread
                </h2>
                
                <button
                  onClick={onClose}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#888',
                    cursor: 'pointer',
                    padding: '8px'
                  }}
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Thread Content */}
            <div style={{ padding: '16px' }}>
              {/* Thread Info Card */}
              <div style={{
                background: colors.bgTertiary1,
                borderRadius: '12px',
                padding: '16px',
                border: '2px solid #333',
                marginBottom: '16px'
              }}>
                {/* Room/Thread Type */}
                {thread.sesh_room_info ? (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    marginBottom: '12px',
                    padding: '6px 10px',
                    background: 'linear-gradient(135deg, #3b82f620 0%, #3b82f610 100%)',
                    border: '1px solid #3b82f640',
                    borderRadius: '8px'
                  }}>
                    <Users size={12} style={{ color: '#3b82f6' }} />
                    <span style={{ fontSize: '11px', fontWeight: '500', color: '#3b82f6' }}>
                      {thread.sesh_room_info.name}
                    </span>
                  </div>
                ) : (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    marginBottom: '12px',
                    padding: '6px 10px',
                    background: 'linear-gradient(135deg, #10b98120 0%, #10b98110 100%)',
                    border: '1px solid #10b98140',
                    borderRadius: '8px'
                  }}>
                    <UserCircle size={12} style={{ color: '#10b981' }} />
                    <span style={{ fontSize: '11px', fontWeight: '500', color: '#10b981' }}>
                      Personal Thread
                    </span>
                  </div>
                )}

                {/* Title */}
                <h1 style={{
                  margin: '0 0 12px 0',
                  color: '#e5e5e5',
                  fontSize: '20px',
                  fontWeight: '700',
                  lineHeight: '1.3'
                }}>
                  {formatContentWithHashtags(threadData.title, handleHashtagClick)}
                </h1>

                {/* Description */}
                {threadData.description && (
                  <p style={{
                    margin: '0 0 12px 0',
                    color: colors.textPrimary1,
                    fontSize: '14px',
                    lineHeight: '1.5'
                  }}>
                    {formatContentWithHashtags(threadData.description, handleHashtagClick)}
                  </p>
                )}

                {/* Author Section */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '12px',
                  padding: '12px',
                  background: '#0a0a0a',
                  borderRadius: '8px'
                }}>
                  <img
                    src={thread.avatar || thread.user_avatar}
                    alt={thread.author}
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '8px',
                      objectFit: 'cover',
                      cursor: thread.author_user_id !== currentUserId ? 'pointer' : 'default'
                    }}
                    onClick={() => handleUsernameClick(thread.author_user_id)}
                  />
                  
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '12px', color: '#888', marginBottom: '2px' }}>
                      By:
                    </div>
                    <span 
                      style={{ 
                        fontSize: '14px', 
                        color: '#e5e5e5',
                        fontWeight: '600',
                        cursor: thread.author_user_id !== currentUserId ? 'pointer' : 'default',
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                      onClick={() => handleUsernameClick(thread.author_user_id)}
                    >
                      {thread.author_handle || thread.author}
                    </span>
                  </div>

                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '4px 8px',
                    background: 'linear-gradient(135deg, #00d4aa20 0%, #10b98120 100%)',
                    border: '1px solid #00d4aa40',
                    borderRadius: '8px'
                  }}>
                    <img 
                      src={getSmokerEmoji(thread.author_smoke_level)}
                      alt={`${thread.author_smoke_level} level`}
                      style={{
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        objectFit: 'cover'
                      }}
                    />
                  </div>
                </div>

                {/* Stats */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '8px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleVote(thread.user_vote === 1 ? 0 : 1);
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '6px 8px',
                          background: thread.user_vote === 1 
                        ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                        : colors.upvote1,
                          border: '1px solid',
                          borderColor: thread.user_vote === 1 ? '#00d4aa' : '#333',
                          borderRadius: '8px',
                          color: thread.user_vote === 1 ? 'white' : colors.upvote1,
                          cursor: 'pointer',
                          fontSize: '11px',
                          fontWeight: '500',
                          transition: 'all 0.3s ease'
                        }}
                      >
                        <ArrowUp size={12} />
                        <span>{thread.likes}</span>
                      </button>
                      
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleVote(thread.user_vote === -1 ? 0 : -1);
                        }}
                         style={{
                          padding: '6px',
                          background: thread.user_vote === -1 
                            ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                            : 'transparent',
                          border: '1px solid',
                          borderColor: thread.user_vote === -1 ? '#ef4444' : '#333',
                          borderRadius: '8px',
                          color: thread.user_vote === -1 ? 'white' : '#888',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease'
                        }}
                      >
                        <ArrowDown size={12} />
                      </button>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#888' }}>
                      <MessageCircle size={14} />
                      <span style={{ fontSize: '12px' }}>
                        {threadData.messages?.length || 0}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Reply Box (if replying) - Mobile */}
              {replyingTo && (
                <div style={{
                  background: colors.bgCard,
                  borderRadius: '12px',
                  padding: '12px',
                  border: '2px solid #8b5cf6',
                  marginBottom: '16px'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '8px'
                  }}>
                    <span style={{ color: '#8b5cf6', fontSize: '12px', fontWeight: '500' }}>
                      Replying to {replyingTo.username}
                    </span>
                    <button
                      onClick={() => {
                        setReplyingTo(null);
                        setSelectedGif(null);
                        setReplyContent('');
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#888',
                        cursor: 'pointer'
                      }}
                    >
                      <X size={14} />
                    </button>
                  </div>
                  
                  {/* Show selected GIF preview or textarea */}
                  {selectedGif && isReplyGiphy ? (
                    <div style={{ position: 'relative', marginBottom: '8px' }}>
                      <img
                        src={selectedGif.images.fixed_height_small.url}
                        alt="Selected GIF"
                        style={{
                          width: '100%',
                          maxWidth: '200px',
                          borderRadius: '8px'
                        }}
                      />
                      <button
                        onClick={clearSelectedGif}
                        style={{
                          position: 'absolute',
                          top: '8px',
                          right: '8px',
                          background: 'rgba(0, 0, 0, 0.8)',
                          border: 'none',
                          borderRadius: '50%',
                          width: '24px',
                          height: '24px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          color: 'white'
                        }}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <textarea
                      value={replyContent}
                      onChange={(e) => setReplyContent(e.target.value)}
                      placeholder="Write your reply..."
                      rows={3}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #333',
                        borderRadius: '8px',
                        background: colors.bgTertiary1,
                        color: '#e5e5e5',
                        resize: 'vertical',
                        outline: 'none',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }}
                    />
                  )}
                  
                  <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                    <button
                      onClick={() => openGiphyPicker(true)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '8px 14px',
                        background: 'transparent',
                        border: '1px solid #333',
                        borderRadius: '8px',
                        color: '#888',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: '500'
                      }}
                    >
                      <ImageIcon size={12} />
                      GIF
                    </button>
                    
                    <button
                      onClick={handlePostReply}
                      disabled={!replyContent.trim()}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        flex: 1,
                        padding: '8px 14px',
                        background: replyContent.trim() 
                          ? 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)'
                          : '#333',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: replyContent.trim() ? 'pointer' : 'not-allowed',
                        fontSize: '13px',
                        fontWeight: '500',
                        justifyContent: 'center'
                      }}
                    >
                      <Send size={12} />
                      Reply
                    </button>
                  </div>
                </div>
              )}

              {/* Messages Container - Mobile */}
              <div style={{ 
                marginBottom: '16px',
                background: colors.bgTertiary1,
                borderRadius: '12px',
                padding: '12px'
              }}>
                {threadData.messages && threadData.messages.length > 0 ? (
                  threadData.messages.map((message) => (
                    <MessageComponent key={message.id} message={message} />
                  ))
                ) : (
                  <div style={{
                    textAlign: 'center',
                    padding: '32px 16px',
                    background: colors.bgTertiary1,
                    borderRadius: '12px',
                    border: '1px solid #333'
                  }}>
                    <MessageCircle size={36} style={{ color: '#666', marginBottom: '12px' }} />
                    <h3 style={{ margin: '0 0 6px 0', color: '#e5e5e5', fontSize: '16px' }}>No messages yet</h3>
                    <p style={{ margin: 0, color: '#888', fontSize: '13px' }}>Be the first to start the discussion!</p>
                  </div>
                )}
              </div>
            </div>

            {/* New Message Box - Mobile Fixed Bottom */}
            <div style={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              background: colors.bgTertiary1,
              borderTop: '2px solid #333',
              padding: '12px 16px',
              zIndex: 9
            }}>
              {/* Show selected GIF preview or textarea */}
              {selectedGif && !isReplyGiphy ? (
                <div style={{ position: 'relative', marginBottom: '8px' }}>
                  <img
                    src={selectedGif.images.fixed_height_small.url}
                    alt="Selected GIF"
                    style={{
                      width: '100%',
                      maxWidth: '200px',
                      borderRadius: '8px'
                    }}
                  />
                  <button
                    onClick={clearSelectedGif}
                    style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      background: 'rgba(0, 0, 0, 0.8)',
                      border: 'none',
                      borderRadius: '50%',
                      width: '24px',
                      height: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      color: 'white'
                    }}
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Add to the discussion..."
                  rows={2}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #333',
                    borderRadius: '10px',
                    background: colors.bgCard,
                    color: '#e5e5e5',
                    resize: 'none',
                    outline: 'none',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    marginBottom: '8px'
                  }}
                />
              )}
              
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => openGiphyPicker(false)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '12px',
                    background: 'transparent',
                    border: '1px solid #333',
                    borderRadius: '10px',
                    color: '#888',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <ImageIcon size={16} />
                </button>
                
                <button
                  onClick={handlePostMessage}
                  disabled={!newMessage.trim()}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    flex: 1,
                    padding: '12px',
                    background: newMessage.trim() 
                      ? 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)'
                      : '#333',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    cursor: newMessage.trim() ? 'pointer' : 'not-allowed',
                    fontSize: '15px',
                    fontWeight: '600',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <Send size={16} />
                  Post
                </button>
              </div>
            </div>
          </div>

          {showProfileView && selectedUserId && (
            <PublicProfileView
              userId={selectedUserId}
              currentUserId={currentUserId}
              onClose={() => {
                setShowProfileView(false);
                setSelectedUserId(null);
              }}
            />
          )}
        </div>

        {/* GIPHY Picker Modal */}
        {showGiphyPicker && <GiphyPicker />}
      </>
    );
  }
    return (
    <>
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.95)',
      zIndex: 1000,
      overflow: 'auto'
    }}>
      <div style={{
        maxWidth: '800px',
        margin: '0 auto',
        padding: '24px',
        minHeight: '100vh',
        background: colors.bgTertiary1,
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '24px',
          padding: '16px 0'
        }}>
          <h2 style={{
            margin: 0,
            color: 'white',
            fontSize: '24px',
            fontWeight: '700'
          }}>
            Thread Discussion
          </h2>
          
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#888',
              cursor: 'pointer',
              padding: '8px'
            }}
          >
            <X size={24} />
          </button>
        </div>

        {/* Thread Header */}
        <div style={{
          background: colors.bgTertiary1,
          borderRadius: '16px',
          padding: '24px',
          border: '2px solid #333',
          marginBottom: '24px'
        }}>
          {/* Sesh Room Indicator */}
          {thread.sesh_room_info ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '16px',
              padding: '8px 12px',
              background: 'linear-gradient(135deg, #3b82f620 0%, #3b82f610 100%)',
              border: '1px solid #3b82f640',
              borderRadius: '12px'
            }}>
              <Users size={14} style={{ color: '#3b82f6' }} />
              <span style={{ fontSize: '12px', fontWeight: '500', color: '#3b82f6' }}>
                {thread.sesh_room_info.name}
              </span>
            </div>
          ) : (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '16px',
              padding: '8px 12px',
              background: 'linear-gradient(135deg, #10b98120 0%, #10b98110 100%)',
              border: '1px solid #10b98140',
              borderRadius: '12px'
            }}>
              <UserCircle size={14} style={{ color: '#10b981' }} />
              <span style={{ fontSize: '12px', fontWeight: '500', color: '#10b981' }}>
                Personal Thread
              </span>
            </div>
          )}

          {/* Main Content Area - Title and Author Side by Side */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '24px', marginBottom: '16px' }}>
            
            {/* Left Side - Thread Title and Description */}
            <div style={{ flex: 1 }}>
              <h1 style={{
                margin: '0 0 12px 0',
                color: '#e5e5e5',
                fontSize: '28px',
                fontWeight: '700',
                lineHeight: '1.3'
              }}>
                {formatContentWithHashtags(threadData.title, handleHashtagClick)}
              </h1>

              {threadData.description && (
                <p style={{
                  margin: '0',
                  color: colors.textPrimary1,
                  fontSize: '16px',
                  lineHeight: '1.5'
                }}>
                  {formatContentWithHashtags(threadData.description, handleHashtagClick)}
                </p>
              )}
            </div>

            {/* Right Side - Author Info */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'flex-start', 
              gap: '16px',
              minWidth: '300px',
              flexShrink: 0
            }}>
              
              {/* Avatar */}
              <div style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center'
              }}>    
                <span 
                  className={thread.author_user_id !== currentUserId ? 'clickable-username' : ''}
                  style={{ 
                    fontSize: '16px', 
                    color: '#e5e5e5',
                    fontWeight: '600',
                    cursor: thread.author_user_id !== currentUserId ? 'pointer' : 'default',
                    display: 'block',
                    marginBottom: '4px',
                    marginRight: '2%'
                  }}
                  onClick={() => handleUsernameClick(thread.author_user_id)}
                >
                  By: {thread.author_handle || thread.author}
                </span>
                <img
                  src={thread.avatar || thread.user_avatar}
                  alt={thread.author}
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    objectFit: 'cover',
                    cursor: thread.author_user_id !== currentUserId ? 'pointer' : 'default'
                  }}
                  onClick={() => handleUsernameClick(thread.author_user_id)}
                />
              </div>

              {/* Author Details */}
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  
                  {/* Smoker Level Badge */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '4px 8px',
                    background: 'linear-gradient(135deg, #00d4aa20 0%, #10b98120 100%)',
                    border: '1px solid #00d4aa40',
                    borderRadius: '12px'
                  }}>
                    <img 
                      src={getSmokerEmoji(thread.author_smoke_level)}
                      alt={`${thread.author_smoke_level} level`}
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        objectFit: 'cover'
                      }}
                    />
                    <span style={{
                      fontSize: '11px',
                      fontWeight: '500',
                      color: '#00d4aa',
                      textTransform: 'capitalize'
                    }}>
                      {thread.author_smoke_level || 'Member'}
                    </span>
                  </div>

                </div>

              </div>
            </div>
          </div>

          {/* Thread Stats */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <button
                    onClick={(e) => {
                    e.stopPropagation();
                    handleVote(thread.user_vote === 1 ? 0 : 1);
                    }}
                    style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '6px 8px',
                    background: thread.user_vote === 1 
                      ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                      : colors.upvote1,
                    border: '1px solid',
                    borderColor: thread.user_vote === 1 ? '#00d4aa' : '#333',
                    borderRadius: '8px',
                    color: thread.user_vote === 1 ? 'white' : colors.upvote1,
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: '500',
                    transition: 'all 0.3s ease'
                    }}
                >
                    <ArrowUp size={14} />
                    <span>{thread.likes}</span>
                </button>
                
                <button
                    onClick={(e) => {
                    e.stopPropagation();
                    handleVote(thread.user_vote === -1 ? 0 : -1);
                    }}
                    style={{
                    padding: '6px',
                    background: thread.user_vote === -1 
                        ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                        : 'transparent',
                    border: '1px solid',
                    borderColor: thread.user_vote === -1 ? '#ef4444' : '#333',
                    borderRadius: '8px',
                    color: thread.user_vote === -1 ? 'white' : '#888',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                    }}
                >
                    <ArrowDown size={14} />
                </button>
                </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#888' }}>
                <MessageCircle size={16} />
                <span style={{ fontSize: '14px' }}>
                  {threadData.messages?.length || 0} messages
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Reply Box (if replying) */}
        {replyingTo && (
          <div style={{
            background: colors.bgCard,
            borderRadius: '12px',
            padding: '16px',
            border: '2px solid #8b5cf6',
            marginBottom: '24px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '12px'
            }}>
              <span style={{ color: '#8b5cf6', fontSize: '14px', fontWeight: '500' }}>
                Replying to {replyingTo.username}
              </span>
              <button
                onClick={() => {
                  setReplyingTo(null);
                  setSelectedGif(null);
                  setReplyContent('');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#888',
                  cursor: 'pointer'
                }}
              >
                <X size={16} />
              </button>
            </div>
            
            {/* Show selected GIF preview or textarea */}
            {selectedGif && isReplyGiphy ? (
              <div style={{ position: 'relative', marginBottom: '12px' }}>
                <img
                  src={selectedGif.images.fixed_height_small.url}
                  alt="Selected GIF"
                  style={{
                    maxWidth: '300px',
                    borderRadius: '8px'
                  }}
                />
                <button
                  onClick={clearSelectedGif}
                  style={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    background: 'rgba(0, 0, 0, 0.8)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '28px',
                    height: '28px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    color: 'white'
                  }}
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <textarea
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
                placeholder="Write your reply..."
                rows={3}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #333',
                  borderRadius: '8px',
                  background: colors.bgTertiary1,
                  color: '#e5e5e5',
                  resize: 'vertical',
                  outline: 'none',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            )}
            
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '12px' }}>
              <button
                onClick={() => openGiphyPicker(true)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 16px',
                  background: 'transparent',
                  border: '1px solid #333',
                  borderRadius: '8px',
                  color: '#888',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#00d4aa';
                  e.currentTarget.style.color = '#00d4aa';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#333';
                  e.currentTarget.style.color = '#888';
                }}
              >
                <ImageIcon size={14} />
                Add GIF
              </button>
              
              <button
                onClick={handlePostReply}
                disabled={!replyContent.trim()}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 16px',
                  background: replyContent.trim() 
                    ? 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)'
                    : '#333',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: replyContent.trim() ? 'pointer' : 'not-allowed',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                <Send size={14} />
                Reply
              </button>
            </div>
          </div>
        )}

        {/* Messages Container with consistent background */}
        <div style={{ 
          marginBottom: '24px',
          background: colors.bgTertiary1,
          borderRadius: '12px',
          padding: '16px'
        }}>
          {threadData.messages && threadData.messages.length > 0 ? (
            threadData.messages.map((message) => (
              <MessageComponent key={message.id} message={message} />
            ))
          ) : (
            <div style={{
              textAlign: 'center',
              padding: '48px 24px',
              background: colors.bgTertiary1,
              borderRadius: '12px',
              border: '1px solid #333'
            }}>
              <MessageCircle size={48} style={{ color: '#666', marginBottom: '16px' }} />
              <h3 style={{ margin: '0 0 8px 0', color: '#e5e5e5' }}>No messages yet</h3>
              <p style={{ margin: 0, color: '#888' }}>Be the first to start the discussion!</p>
            </div>
          )}
        </div>

        {/* New Message Box */}
        <div style={{
          background: colors.bgTertiary1,
          borderRadius: '16px',
          padding: '20px',
          border: '2px solid #333',
          position: 'sticky',
          bottom: '0'
        }}>
          {/* Show selected GIF preview or textarea */}
          {selectedGif && !isReplyGiphy ? (
            <div style={{ position: 'relative', marginBottom: '12px' }}>
              <img
                src={selectedGif.images.fixed_height_small.url}
                alt="Selected GIF"
                style={{
                  maxWidth: '300px',
                  borderRadius: '12px'
                }}
              />
              <button
                onClick={clearSelectedGif}
                style={{
                  position: 'absolute',
                  top: '8px',
                  right: '8px',
                  background: 'rgba(0, 0, 0, 0.8)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '28px',
                  height: '28px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: 'white'
                }}
              >
                <X size={16} />
              </button>
            </div>
          ) : (
            <textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Add to the discussion..."
              rows={3}
              style={{
                width: '100%',
                padding: '16px',
                border: '1px solid #333',
                borderRadius: '12px',
                background: colors.bgCard,
                color: '#e5e5e5',
                resize: 'vertical',
                outline: 'none',
                fontSize: '16px',
                boxSizing: 'border-box',
                marginBottom: '12px'
              }}
            />
          )}
          
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <button
              onClick={() => openGiphyPicker(false)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 24px',
                background: 'transparent',
                border: '1px solid #333',
                borderRadius: '12px',
                color: '#888',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '600',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#00d4aa';
                e.currentTarget.style.color = '#00d4aa';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#333';
                e.currentTarget.style.color = '#888';
              }}
            >
              <ImageIcon size={16} />
              Add GIF
            </button>
            
            <button
              onClick={handlePostMessage}
              disabled={!newMessage.trim()}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 24px',
                background: newMessage.trim() 
                  ? 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)'
                  : '#333',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                cursor: newMessage.trim() ? 'pointer' : 'not-allowed',
                fontSize: '16px',
                fontWeight: '600',
                transition: 'all 0.3s ease'
              }}
            >
              <Send size={16} />
              Post Message
            </button>
          </div>
        </div>
      </div>
      {showProfileView && selectedUserId && (
        <PublicProfileView
          userId={selectedUserId}
          currentUserId={currentUserId}
          onClose={() => {
            setShowProfileView(false);
            setSelectedUserId(null);
          }}
        />
      )}
    </div>
    
    {/* GIPHY Picker Modal */}
    {showGiphyPicker && <GiphyPicker />}
    </>
  );
};

export default ThreadDetailView;